import React, { useState, memo } from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, PackageIcon, ShirtIcon, PlaneIcon, SparklesIcon, ChevronDownIcon, ChevronUpIcon, ClockIcon, TruckIcon, ShieldCheckIcon, BoxIcon, TagIcon, PaletteIcon, FileCheckIcon, FactoryIcon, SearchCheckIcon, ShipIcon, UsersIcon, DollarSignIcon, ZapIcon } from 'lucide-react';
export function CorporateTravelKits() {
  const [expandedKit, setExpandedKit] = useState<string | null>(null);
  const [attendees, setAttendees] = useState(50);
  const [selectedTier, setSelectedTier] = useState('premium');
  const [personalization, setPersonalization] = useState(true);
  const [freightMode, setFreightMode] = useState('sea');
  const kitContents = {
    carryOn: {
      icon: <PackageIcon className="h-8 w-8" data-id="element-1186" />,
      title: 'Carry-on / Weekender',
      image: 'https://images.unsplash.com/photo-1565026057447-bc90a3dceb87?w=800&q=80',
      items: ['Premium carry-on luggage (20-22")', 'Durable wheels and telescoping handle', 'TSA-approved locks', 'Interior organization pockets', 'Your logo embroidered or debossed']
    },
    apparel: {
      icon: <ShirtIcon className="h-8 w-8" data-id="element-1187" />,
      title: 'Apparel Set',
      image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=800&q=80',
      items: ['Branded polo or performance tee', 'Lightweight jacket or quarter-zip', 'Baseball cap or beanie', 'Moisture-wicking socks', 'Size-specific packaging']
    },
    essentials: {
      icon: <PlaneIcon className="h-8 w-8" data-id="element-1188" />,
      title: 'Travel Essentials',
      image: 'https://images.unsplash.com/photo-1544816155-12df9643f363?w=800&q=80',
      items: ['Travel adapter (universal)', 'Portable charger (10,000mAh)', 'Noise-canceling earbuds', 'Eye mask and earplugs', 'Reusable water bottle', 'Travel-size toiletry kit']
    },
    keepsake: {
      icon: <SparklesIcon className="h-8 w-8" data-id="element-1189" />,
      title: 'Signature Keepsake',
      image: 'https://images.unsplash.com/photo-1517842645767-c639042777db?w=800&q=80',
      items: ['Premium notebook with custom cover', 'Engraved pen set', 'Custom challenge coin or pin', 'Personalized name badge holder', 'Event-specific commemorative item']
    }
  };
  const tiers = [{
    id: 'core',
    name: 'Core Kit',
    badge: null,
    description: 'Apparel + essentials + keepsake',
    leadTime: '6-8 weeks',
    includes: ['Branded apparel set (3 items)', 'Travel essentials bundle', 'Signature keepsake item', 'Custom packaging', 'Bulk shipment to HQ'],
    excludes: ['Carry-on luggage', 'Personal item bag'],
    priceRange: '$75-125'
  }, {
    id: 'premium',
    name: 'Premium Kit',
    badge: 'Most Popular',
    description: 'Carry-on + apparel + essentials + keepsake',
    leadTime: '8-10 weeks',
    includes: ['Premium carry-on luggage', 'Branded apparel set (3 items)', 'Complete travel essentials', 'Signature keepsake item', 'Optional name/role tags', 'Packing cubes included', 'Custom packaging'],
    excludes: [],
    priceRange: '$175-275'
  }, {
    id: 'exec',
    name: 'Executive Kit',
    badge: null,
    description: 'Premium carry-on + personal item + elevated materials',
    leadTime: '10-12 weeks',
    includes: ['Premium carry-on (higher grade)', 'Matching personal item bag', 'Luxury apparel set (4+ items)', 'Premium travel essentials', 'Executive keepsake collection', 'Full personalization (name embroidery)', 'Premium packing cubes', 'Gift-quality packaging'],
    excludes: [],
    priceRange: '$325-475'
  }];
  const timeline = [{
    step: 1,
    icon: <BoxIcon className="h-6 w-6" data-id="element-1190" />,
    title: 'Select Tier + Quantity',
    description: 'Choose kit level and set ship-to-HQ date'
  }, {
    step: 2,
    icon: <PaletteIcon className="h-6 w-6" data-id="element-1191" />,
    title: 'Upload Branding',
    description: 'Logo, event name, and color palette'
  }, {
    step: 3,
    icon: <FileCheckIcon className="h-6 w-6" data-id="element-1192" />,
    title: 'Approve Sample',
    description: 'Physical sample shipped for approval'
  }, {
    step: 4,
    icon: <FactoryIcon className="h-6 w-6" data-id="element-1193" />,
    title: 'Production + Branding',
    description: 'Manufacturing and logo application'
  }, {
    step: 5,
    icon: <SearchCheckIcon className="h-6 w-6" data-id="element-1194" />,
    title: 'QC Inspection',
    description: 'Mandatory quality control before shipping'
  }, {
    step: 6,
    icon: <ShipIcon className="h-6 w-6" data-id="element-1195" />,
    title: 'Bulk Shipment',
    description: 'Consolidated delivery to your HQ'
  }, {
    step: 7,
    icon: <UsersIcon className="h-6 w-6" data-id="element-1196" />,
    title: 'Reorder Window',
    description: 'Late joiners accommodation period'
  }];
  const calculatePrice = () => {
    const basePrices = {
      core: 100,
      premium: 225,
      exec: 400
    };
    let price = basePrices[selectedTier as keyof typeof basePrices];
    // Volume discount
    if (attendees >= 100) price *= 0.85;else if (attendees >= 50) price *= 0.9;
    // Personalization
    if (personalization) price += 15;
    // Freight
    if (freightMode === 'air') price += 25;
    return Math.round(price);
  };
  const getTimeline = () => {
    if (selectedTier === 'core') return '6-8 weeks';
    if (selectedTier === 'premium') return '8-10 weeks';
    return '10-12 weeks';
  };
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-1197">
      {/* Hero */}
      <section className="relative py-24 md:py-32 bg-gradient-to-br from-[#0099A1] to-[#006D73] overflow-hidden" data-id="element-1198">
        <div className="absolute inset-0 opacity-10" data-id="element-1199">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} data-id="element-1200"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1201">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1202">
            <div data-id="element-1203">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-id="element-1204">
                Premium Corporate Travel Kits — Delivered to Your HQ Before
                Departure
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed" data-id="element-1205">
                We source, brand, kit, and bulk-ship attendee kits so your team
                travels with everything.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-1206">
                <Button variant="primary" size="lg" to="/contact" className="bg-white text-[#0099A1] hover:bg-white/90 border-0" data-id="element-1207">
                  Build a Kit
                </Button>
                <Button variant="secondary" size="lg" to="/contact" className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-white/30" data-id="element-1208">
                  Get Quote
                </Button>
              </div>
            </div>
            <div className="relative" data-id="element-1209">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-1210">
                <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80" alt="Travel kit mockup" className="w-full h-[500px] object-cover" data-id="element-1211" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-1212"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8" data-id="element-1213">
                  <div className="grid grid-cols-2 gap-3" data-id="element-1214">
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3" data-id="element-1215">
                      <PackageIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1216" />
                      <span className="text-[#374047] font-semibold text-sm" data-id="element-1217">
                        Carry-on
                      </span>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3" data-id="element-1218">
                      <ShirtIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1219" />
                      <span className="text-[#374047] font-semibold text-sm" data-id="element-1220">
                        Apparel
                      </span>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3" data-id="element-1221">
                      <PlaneIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1222" />
                      <span className="text-[#374047] font-semibold text-sm" data-id="element-1223">
                        Essentials
                      </span>
                    </div>
                    <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 flex items-center gap-3" data-id="element-1224">
                      <SparklesIcon className="h-6 w-6 text-[#0099A1]" data-id="element-1225" />
                      <span className="text-[#374047] font-semibold text-sm" data-id="element-1226">
                        Keepsake
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-20 bg-white" data-id="element-1227">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1228">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1229">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1230">
              What's Inside the Kit
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1231">
              Every kit is thoughtfully curated with premium items your team
              will actually use
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-1232">
            {Object.entries(kitContents).map(([key, content]) => <div key={key} className="group" data-id="element-1233">
                <div className="relative overflow-hidden rounded-2xl mb-4 shadow-lg" data-id="element-1234">
                  <img src={content.image} alt={content.title} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-1235" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-1236"></div>
                  <div className="absolute bottom-4 left-4 right-4" data-id="element-1237">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-[#0099A1] mb-3" data-id="element-1238">
                      {content.icon}
                    </div>
                    <h3 className="text-lg font-bold text-white" data-id="element-1239">
                      {content.title}
                    </h3>
                  </div>
                </div>
                <button onClick={() => setExpandedKit(expandedKit === key ? null : key)} className="flex items-center gap-2 text-[#0099A1] font-medium text-sm hover:text-[#006D73] transition-colors" data-id="element-1240">
                  <span data-id="element-1241">View Details</span>
                  {expandedKit === key ? <ChevronUpIcon className="h-4 w-4" data-id="element-1242" /> : <ChevronDownIcon className="h-4 w-4" data-id="element-1243" />}
                </button>
              </div>)}
          </div>

          {/* Expandable Checklist */}
          {expandedKit && <div className="max-w-4xl mx-auto bg-white rounded-2xl p-8 border-2 border-[#0099A1]/20 shadow-lg" data-id="element-1244">
              <h4 className="text-2xl font-bold text-[#374047] mb-6 flex items-center gap-3" data-id="element-1245">
                {kitContents[expandedKit as keyof typeof kitContents].icon}
                {kitContents[expandedKit as keyof typeof kitContents].title}
              </h4>
              <ul className="space-y-4" data-id="element-1246">
                {kitContents[expandedKit as keyof typeof kitContents].items.map((item, index) => <li key={index} className="flex items-start gap-4" data-id="element-1247">
                      <CheckIcon className="h-6 w-6 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1248" />
                      <span className="text-[#374047] text-lg" data-id="element-1249">{item}</span>
                    </li>)}
              </ul>
            </div>}
        </div>
      </section>

      {/* Kit Tiers */}
      <section className="py-20 bg-[#F4E8D0]/30" data-id="element-1250">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1251">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1252">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1253">
              Choose Your Kit Tier
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1254">
              Select the package that fits your event and budget
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1255">
            {tiers.map(tier => <Card key={tier.id} className={`border-2 ${tier.badge ? 'border-[#0099A1] shadow-2xl scale-105' : 'border-gray-200'} relative overflow-hidden`} data-id="element-1256">
                {tier.badge && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10" data-id="element-1257">
                    <span className="inline-flex items-center px-6 py-2 bg-[#FF6B65] text-white text-sm font-bold rounded-full shadow-lg" data-id="element-1258">
                      {tier.badge}
                    </span>
                  </div>}
                <CardHeader className={`${tier.badge ? 'bg-[#0099A1] pt-8' : 'bg-[#374047]'} text-white p-8`} data-id="element-1259">
                  <h3 className="text-2xl font-bold mb-3" data-id="element-1260">{tier.name}</h3>
                  <p className="text-white/90 mb-4" data-id="element-1261">{tier.description}</p>
                  <div className="flex items-center gap-2 text-sm bg-white/20 rounded-lg px-3 py-2 w-fit" data-id="element-1262">
                    <ClockIcon className="h-4 w-4" data-id="element-1263" />
                    <span data-id="element-1264">Lead time: {tier.leadTime}</span>
                  </div>
                </CardHeader>
                <CardBody className="p-8" data-id="element-1265">
                  <div className="text-center mb-8 pb-8 border-b-2 border-gray-100" data-id="element-1266">
                    <div className="text-4xl font-bold text-[#374047] mb-2" data-id="element-1267">
                      {tier.priceRange}
                    </div>
                    <div className="text-sm text-[#374047]/60" data-id="element-1268">per kit</div>
                  </div>

                  <h4 className="font-bold text-[#374047] mb-4 text-lg" data-id="element-1269">
                    What's Included:
                  </h4>
                  <ul className="space-y-3 mb-8" data-id="element-1270">
                    {tier.includes.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-1271">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-1272" />
                        <span className="text-[#374047]" data-id="element-1273">{item}</span>
                      </li>)}
                  </ul>

                  {tier.excludes.length > 0 && <>
                      <h4 className="font-bold text-[#374047]/60 mb-4" data-id="element-1274">
                        Not Included:
                      </h4>
                      <ul className="space-y-2 mb-8" data-id="element-1275">
                        {tier.excludes.map((item, index) => <li key={index} className="flex items-start gap-3 text-[#374047]/60" data-id="element-1276">
                            <span className="text-lg" data-id="element-1277">•</span>
                            <span data-id="element-1278">{item}</span>
                          </li>)}
                      </ul>
                    </>}

                  <Button variant={tier.badge ? 'primary' : 'secondary'} className="w-full" to="/contact" data-id="element-1279">
                    Select {tier.name}
                  </Button>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white" data-id="element-1280">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1281">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1282">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1283">
              How It Works
            </h2>
            <p className="text-lg text-[#374047]/70" data-id="element-1284">
              Direct sourcing timeline from selection to delivery
            </p>
          </div>

          <div className="max-w-6xl mx-auto" data-id="element-1285">
            <div className="grid md:grid-cols-7 gap-6" data-id="element-1286">
              {timeline.map((item, index) => <div key={item.step} className="relative" data-id="element-1287">
                  <div className="flex flex-col items-center text-center" data-id="element-1288">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-full flex items-center justify-center text-white font-bold text-2xl mb-4 shadow-lg" data-id="element-1289">
                      {item.step}
                    </div>
                    <div className="w-14 h-14 bg-[#0099A1]/10 rounded-full flex items-center justify-center text-[#0099A1] mb-4" data-id="element-1290">
                      {item.icon}
                    </div>
                    <h4 className="font-bold text-[#374047] mb-2" data-id="element-1291">
                      {item.title}
                    </h4>
                    <p className="text-sm text-[#374047]/60" data-id="element-1292">
                      {item.description}
                    </p>
                  </div>
                  {index < timeline.length - 1 && <div className="hidden md:block absolute top-10 left-full w-full h-1 bg-gradient-to-r from-[#0099A1] to-[#0099A1]/20 -z-10" data-id="element-1293"></div>}
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Lead Time */}
      <section className="py-20 bg-[#F4E8D0]/30" data-id="element-1294">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1295">
          <div className="max-w-4xl mx-auto" data-id="element-1296">
            <div className="text-center mb-16" data-id="element-1297">
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1298">
                Lead Time & Reliability
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12" data-id="element-1299">
              <Card className="border-2 border-[#0099A1]/30 shadow-lg" data-id="element-1300">
                <CardBody className="p-10" data-id="element-1301">
                  <div className="flex items-center gap-4 mb-6" data-id="element-1302">
                    <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center" data-id="element-1303">
                      <ClockIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1304" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1305">
                      Standard Timeline
                    </h3>
                  </div>
                  <p className="text-3xl font-bold text-[#0099A1] mb-3" data-id="element-1306">
                    6-10 weeks door-to-door
                  </p>
                  <p className="text-[#374047]/70 text-lg" data-id="element-1307">
                    Recommended for best pricing and full customization options
                  </p>
                </CardBody>
              </Card>

              <Card className="border-2 border-[#FF6B65]/30 shadow-lg" data-id="element-1308">
                <CardBody className="p-10" data-id="element-1309">
                  <div className="flex items-center gap-4 mb-6" data-id="element-1310">
                    <div className="w-16 h-16 bg-[#FF6B65]/10 rounded-full flex items-center justify-center" data-id="element-1311">
                      <ZapIcon className="h-8 w-8 text-[#FF6B65]" data-id="element-1312" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#374047]" data-id="element-1313">
                      Rush Option
                    </h3>
                  </div>
                  <p className="text-3xl font-bold text-[#FF6B65] mb-3" data-id="element-1314">
                    Possible with simpler kit
                  </p>
                  <p className="text-[#374047]/70 text-lg" data-id="element-1315">
                    Higher freight costs apply; limited customization
                  </p>
                </CardBody>
              </Card>
            </div>

            <div className="bg-white rounded-2xl p-10 border-2 border-[#0099A1]/30 shadow-lg" data-id="element-1316">
              <div className="flex items-start gap-6" data-id="element-1317">
                <div className="w-16 h-16 bg-[#0099A1]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1318">
                  <ShieldCheckIcon className="h-8 w-8 text-[#0099A1]" data-id="element-1319" />
                </div>
                <div data-id="element-1320">
                  <h4 className="font-bold text-[#374047] mb-3 text-xl" data-id="element-1321">
                    Quality Control Inspection
                  </h4>
                  <p className="text-[#374047]/70 text-lg" data-id="element-1322">
                    QC inspection is mandatory before shipping. Every kit is
                    checked for quality, branding accuracy, and completeness
                    before leaving the facility.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <section className="py-20 bg-white" data-id="element-1323">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1324">
          <div className="max-w-4xl mx-auto" data-id="element-1325">
            <div className="text-center mb-16" data-id="element-1326">
              <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-1327">
                Estimate Your Kit Cost
              </h2>
              <p className="text-lg text-[#374047]/70" data-id="element-1328">
                Get a quick estimate based on your requirements
              </p>
            </div>

            <Card className="border-2 border-[#0099A1]/30 shadow-xl" data-id="element-1329">
              <CardBody className="p-10" data-id="element-1330">
                <div className="grid md:grid-cols-2 gap-8 mb-10" data-id="element-1331">
                  <div data-id="element-1332">
                    <label className="block text-sm font-bold text-[#374047] mb-3" data-id="element-1333">
                      Number of Attendees
                    </label>
                    <input type="number" value={attendees} onChange={e => setAttendees(parseInt(e.target.value) || 0)} className="w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none text-lg" min="1" data-id="element-1334" />
                  </div>

                  <div data-id="element-1335">
                    <label className="block text-sm font-bold text-[#374047] mb-3" data-id="element-1336">
                      Kit Tier
                    </label>
                    <select value={selectedTier} onChange={e => setSelectedTier(e.target.value)} className="w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:border-[#0099A1] focus:outline-none text-lg" data-id="element-1337">
                      <option value="core" data-id="element-1338">Core Kit</option>
                      <option value="premium" data-id="element-1339">Premium Kit</option>
                      <option value="exec" data-id="element-1340">Executive Kit</option>
                    </select>
                  </div>

                  <div data-id="element-1341">
                    <label className="block text-sm font-bold text-[#374047] mb-3" data-id="element-1342">
                      Personalization
                    </label>
                    <div className="flex gap-4" data-id="element-1343">
                      <button onClick={() => setPersonalization(true)} className={`flex-1 px-6 py-4 rounded-xl border-2 font-semibold transition-colors ${personalization ? 'border-[#0099A1] bg-[#0099A1] text-white' : 'border-gray-200 text-[#374047] hover:border-[#0099A1]/50'}`} data-id="element-1344">
                        Yes
                      </button>
                      <button onClick={() => setPersonalization(false)} className={`flex-1 px-6 py-4 rounded-xl border-2 font-semibold transition-colors ${!personalization ? 'border-[#0099A1] bg-[#0099A1] text-white' : 'border-gray-200 text-[#374047] hover:border-[#0099A1]/50'}`} data-id="element-1345">
                        No
                      </button>
                    </div>
                  </div>

                  <div data-id="element-1346">
                    <label className="block text-sm font-bold text-[#374047] mb-3" data-id="element-1347">
                      Freight Mode
                    </label>
                    <div className="flex gap-4" data-id="element-1348">
                      <button onClick={() => setFreightMode('sea')} className={`flex-1 px-6 py-4 rounded-xl border-2 font-semibold transition-colors ${freightMode === 'sea' ? 'border-[#0099A1] bg-[#0099A1] text-white' : 'border-gray-200 text-[#374047] hover:border-[#0099A1]/50'}`} data-id="element-1349">
                        Sea
                      </button>
                      <button onClick={() => setFreightMode('air')} className={`flex-1 px-6 py-4 rounded-xl border-2 font-semibold transition-colors ${freightMode === 'air' ? 'border-[#0099A1] bg-[#0099A1] text-white' : 'border-gray-200 text-[#374047] hover:border-[#0099A1]/50'}`} data-id="element-1350">
                        Air
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#0099A1] to-[#006D73] rounded-2xl p-10 text-white" data-id="element-1351">
                  <div className="grid md:grid-cols-3 gap-8" data-id="element-1352">
                    <div className="text-center" data-id="element-1353">
                      <div className="text-sm opacity-90 mb-3" data-id="element-1354">
                        Estimated Cost Per Kit
                      </div>
                      <div className="text-5xl font-bold" data-id="element-1355">
                        ${calculatePrice()}
                      </div>
                    </div>
                    <div className="text-center" data-id="element-1356">
                      <div className="text-sm opacity-90 mb-3" data-id="element-1357">
                        Total Estimated Cost
                      </div>
                      <div className="text-5xl font-bold" data-id="element-1358">
                        ${(calculatePrice() * attendees).toLocaleString()}
                      </div>
                    </div>
                    <div className="text-center" data-id="element-1359">
                      <div className="text-sm opacity-90 mb-3" data-id="element-1360">
                        Recommended Timeline
                      </div>
                      <div className="text-5xl font-bold" data-id="element-1361">{getTimeline()}</div>
                    </div>
                  </div>
                  <div className="mt-8 pt-8 border-t border-white/20 text-center" data-id="element-1362">
                    <p className="opacity-90 text-lg" data-id="element-1363">
                      Final pricing depends on exact specifications, branding
                      complexity, and current freight rates
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-24 bg-gradient-to-br from-[#0099A1] to-[#006D73]" data-id="element-1364">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1365">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1366">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1367">
              Get Your Kit Plan in 48 Hours
            </h2>
            <p className="text-2xl text-white/90 mb-10" data-id="element-1368">
              Quote + timeline + sample plan delivered to your inbox
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1369">
              <Button variant="primary" size="lg" to="/contact" className="bg-white text-[#0099A1] hover:bg-white/90 border-0" data-id="element-1370">
                Request Quote
              </Button>
              <Button variant="secondary" size="lg" to="/contact" className="bg-white/20 backdrop-blur-sm border-2 border-white text-white hover:bg-white/30" data-id="element-1371">
                Talk to Ops
              </Button>
            </div>
          </div>
        </div>
      </section>

      <CtaSection data-id="element-1372" />
    </div>;
}